import { isAction, Middleware } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { navigateTo, goBack, resetNavigation } from '../slices/navigationSlice';
import {
  trackScreenChange,
  updateLastActivity,
  trackInteraction,
  saveScrollPosition
} from '../slices/userStateSlice';

interface RecoveryMetadata {
  timestamp: number;
  version: string;
  hasActiveWorkflow: boolean;
  lastScreen: string;
  lastActivity: number;
  navigationHistory: string[];
  activeWorkflowType?: string;
}

/**
 * Navigation middleware that automatically handles state snapshots and recovery metadata
 */
export const navigationMiddleware: Middleware<Record<never, never>, RootState> = (store) => (next) => (action) => {
  const result = next(action);
  const state = store.getState();

  // Middleware can receive non-action values from other dispatch extensions.
  if (!isAction(action)) return result;

  // Check if this is a navigation-related action
  const isNavigationAction = [
    navigateTo.type,
    goBack.type,
    resetNavigation.type,
    trackScreenChange.type,
  ].some(actionType => actionType === action.type);

  if (isNavigationAction) {
    // Trigger recovery snapshot update after navigation changes
    setTimeout(() => {
      updateRecoveryMetadata(state);
    }, 100);

    // Track navigation interaction
    if (navigateTo.match(action) || goBack.match(action)) {
      store.dispatch(trackInteraction({
        type: 'navigation',
        target: navigateTo.match(action) ? action.payload : 'back',
        data: {
          from: state.navigation.previousScreen,
          to: state.navigation.currentScreen,
          timestamp: Date.now(),
        }
      }));
    }

    // Update last activity timestamp
    store.dispatch(updateLastActivity());

    // Save current scroll position when navigating away
    if (navigateTo.match(action) && typeof window !== 'undefined') {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 0) {
        store.dispatch(saveScrollPosition({
          screen: state.navigation.previousScreen || state.navigation.currentScreen,
          position: scrollPosition,
        }));
      }
    }
  }

  // Also handle critical workflow state changes
  const isCriticalStateChange = [
    // Visit-related actions that indicate active workflow
    'visits/createVisit/fulfilled',
    'visits/updateVisit/fulfilled',
    'visits/fetchActiveVisit/fulfilled',
    'visits/endVisit/fulfilled',

    // Evidence-related actions
    'evidence/addEvidence',
    'evidence/removeEvidence',
    'evidence/startRecording',
    'evidence/stopRecording',

    // Customer selection changes
    'customers/setSelectedCustomer',

    // Form state changes
    'userState/updateFormState',
    'userState/saveDraft',
  ].some(actionType => action.type.includes(actionType.split('/')[1]));

  if (isCriticalStateChange) {
    // Delay to ensure state has been updated
    setTimeout(() => {
      updateRecoveryMetadata(store.getState());
    }, 200);
  }

  return result;
};

/**
 * Update recovery metadata in sessionStorage
 */
function updateRecoveryMetadata(state: RootState) {
  try {
    const hasActiveVisit = !!state.visits.activeVisit;
    const hasDrafts = Object.keys(state.userState.drafts).length > 0;
    const hasUnsavedEvidence = state.evidence.evidence.length > 0;
    const hasActiveWorkflow = hasActiveVisit || hasDrafts || hasUnsavedEvidence;

    // Determine active workflow type
    let activeWorkflowType: string | undefined;
    if (hasActiveVisit) {
      activeWorkflowType = 'active_visit';
    } else if (hasUnsavedEvidence) {
      activeWorkflowType = 'unsaved_evidence';
    } else if (hasDrafts) {
      activeWorkflowType = 'form_drafts';
    }

    const metadata: RecoveryMetadata = {
      timestamp: Date.now(),
      version: '1.0',
      hasActiveWorkflow,
      lastScreen: state.navigation.currentScreen,
      lastActivity: state.userState.lastActivity,
      navigationHistory: state.navigation.navigationHistory.slice(-10), // Keep last 10 entries
      activeWorkflowType,
    };

    // Store in sessionStorage for immediate recovery
    sessionStorage.setItem('state-recovery-metadata', JSON.stringify(metadata));

    // Also store a simplified version in localStorage for longer-term recovery
    const persistentMetadata = {
      timestamp: metadata.timestamp,
      hasActiveWorkflow: metadata.hasActiveWorkflow,
      lastScreen: metadata.lastScreen,
      activeWorkflowType: metadata.activeWorkflowType,
    };
    localStorage.setItem('app-recovery-metadata', JSON.stringify(persistentMetadata));

    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Recovery metadata updated:', metadata);
    }
  } catch (error) {
    console.warn('Failed to update recovery metadata:', error);
  }
}

/**
 * Browser navigation middleware - handles browser back/forward buttons
 */
export const browserNavigationMiddleware: Middleware<Record<never, never>, RootState> = (store) => {
  // Set up popstate event listener when middleware is initialized
  if (typeof window !== 'undefined') {
    let isInitialized = false;

    const handlePopState = (event: PopStateEvent) => {
      const state = store.getState();

      if (event.state && event.state.screen) {
        // Navigate to the screen stored in browser history
        store.dispatch(navigateTo(event.state.screen));
      } else {
        // Handle browser back when no state is available
        if (state.navigation.previousScreen) {
          store.dispatch(goBack());
        } else {
          // Fallback to dashboard
          store.dispatch(navigateTo('dashboard'));
        }
      }

      // Track the browser navigation
      store.dispatch(trackInteraction({
        type: 'navigation',
        target: 'browser_back_forward',
        data: {
          state: event.state,
          timestamp: Date.now(),
        }
      }));
    };

    // Initialize listener only once
    if (!isInitialized) {
      window.addEventListener('popstate', handlePopState);
      isInitialized = true;
    }
  }

  return (next) => (action) => {
    const result = next(action);

    // Update browser history when navigation changes
    if (navigateTo.match(action) && typeof window !== 'undefined') {
      const screen = action.payload;
      const url = new URL(window.location.href);
      url.hash = `screen=${screen}`;

      // Push state to browser history
      window.history.pushState(
        { screen, timestamp: Date.now() },
        '',
        url.toString()
      );
    }

    return result;
  };
};

/**
 * Workflow detection middleware - identifies when user enters/exits critical workflows
 */
export const workflowDetectionMiddleware: Middleware<Record<never, never>, RootState> = (store) => (next) => (action) => {
  const prevState = store.getState();
  const result = next(action);
  const newState = store.getState();

  // Detect workflow state changes
  const prevHasActiveVisit = !!prevState.visits.activeVisit;
  const newHasActiveVisit = !!newState.visits.activeVisit;

  const prevHasEvidence = prevState.evidence.evidence.length > 0;
  const newHasEvidence = newState.evidence.evidence.length > 0;

  const prevHasDrafts = Object.keys(prevState.userState.drafts).length > 0;
  const newHasDrafts = Object.keys(newState.userState.drafts).length > 0;

  // Detect workflow start
  if (!prevHasActiveVisit && newHasActiveVisit) {
    console.log('🎯 Active visit workflow started');
    store.dispatch(trackInteraction({
      type: 'navigation',
      target: 'workflow_start',
      data: { workflowType: 'active_visit', timestamp: Date.now() }
    }));
  }

  // Detect workflow end
  if (prevHasActiveVisit && !newHasActiveVisit) {
    console.log('✅ Active visit workflow completed');
    store.dispatch(trackInteraction({
      type: 'navigation',
      target: 'workflow_end',
      data: { workflowType: 'active_visit', timestamp: Date.now() }
    }));
  }

  // Detect evidence workflow changes
  if (!prevHasEvidence && newHasEvidence) {
    console.log('📸 Evidence capture workflow started');
  }

  if (prevHasEvidence && !newHasEvidence) {
    console.log('✅ Evidence capture workflow completed');
  }

  // Detect draft workflow changes
  if (!prevHasDrafts && newHasDrafts) {
    console.log('📝 Form draft workflow started');
  }

  if (prevHasDrafts && !newHasDrafts) {
    console.log('✅ Form draft workflow completed');
  }

  return result;
};

// Export a combined middleware that includes all navigation-related middleware
export const enhancedNavigationMiddleware = [
  navigationMiddleware,
  browserNavigationMiddleware,
  workflowDetectionMiddleware,
];